import {
  DataTablePrimeReact,
  OptionsMenuStorybook,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import {
  faFileExcel,
  faFilePdf,
  faFileXml,
} from "@fortawesome/pro-light-svg-icons"
import { FacturaReadingFiltersDtoProps } from "api/DTOs/FacturaReadingFiltersDto"
import { FacturaReadingSimpleDtoProps } from "api/DTOs/FacturaReadingSimpleDto"
import {
  deleteFacturaById,
  FacturasByFilterQueryParamsProps,
  getFacturasByFilter,
  getFacturasByIdSimple,
} from "api/Factura.service"
import { TIPO_DOCUMENTO_COMERCIAL } from "api/Types/TipoDocumentoComercial"
import { TIPO_DOCUMENTO_ENUM } from "api/Types/TipoDocumentoEnum"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import axios from "axios"
import DeleteModal from "common/components/Modal/DeleteModal"
import ModalVerDocumento from "common/components/ModalVerDocumento/ModalVerDocumento"
import { STATUSCORRECT } from "common/components/ModalVerDocumento/constants"
import NoDataTemplate from "common/components/NoDataTemplate"
import { STATIC_ASSETS_URL } from "common/context/constants"
import notification from "common/utils/notification"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import PagoCobroManualForm from "features/Documentos/components/ModalPagoCobroManual/ModalPagoCobroManual"
import ModalTypeDownloadDocument from "features/Documentos/components/ModalTypeDownloadDocument/ModalTypeDownloadDocument"
import { ModalTypeDownloadDocumentProps } from "features/Documentos/components/ModalTypeDownloadDocument/constant"
import { useFilterMenu } from "hooks/useFilterMenu"
import { Skeleton } from "primereact/skeleton"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { handlerExportFacturasToExcel } from "../FacturasEnviadas/contants/constants"
import FilterMenu from "./FilterMenu"
import { COLUMNAS_FACTURAS_RECIBIDAS_ERP } from "./constants/FacturasRecibidasColumns"

const FacturasRecibidas = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const [sortingCriteria, setSortingCriteria] = useState<
    FacturasByFilterQueryParamsProps["SortingCriteria"]
  >({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [showModalDownload, setShowModalDownload] = useState<
    ModalTypeDownloadDocumentProps["modalState"]
  >({
    show: false,
    id: null,
    serie: null,
  })
  const [showModalPagoCobroManual, setShowModalPagoCobroManual] = useState<{
    show: boolean
    id: string | undefined
    serie: string | null | undefined
  }>({ show: false, id: undefined, serie: undefined })
  const [modalDocumentoDetail, setModalDocumentoDetail] = useState<{
    show: boolean
    document: FacturaReadingSimpleDtoProps | null
  }>({
    show: false,
    document: null,
  })
  const [showDeleteModal, setShowDeleteModal] = useState({
    id: "",
    show: false,
  })
  const [deleting, setDeleting] = useState(false)
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const [isLoadingExport, setIsLoadingExport] = useState(false)

  const dtRef =
    useRef<DataTablePrimeReact<FacturaReadingFiltersDtoProps[]>>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const {
    activeCompany: { id: empresaActivaId },
  } = useUsuarioEmpresaActiva()

  const recibidosCancelToken = useRef(axios.CancelToken.source())
  const fetchItems = useCallback(() => {
    recibidosCancelToken.current?.cancel()
    recibidosCancelToken.current = axios.CancelToken.source()
    return (queryState: FacturasByFilterQueryParamsProps) =>
      getFacturasByFilter(
        {
          ...queryState,
          LibradoId: empresaActivaId,
          SortingCriteria:
            queryState.SortingCriteria ??
            "FechaEmision DESC, CreationTime DESC",
        },
        recibidosCancelToken.current.token
      )
  }, [empresaActivaId])

  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchItems,
    })

  const onRowClick = async ({
    data,
  }: {
    data: FacturaReadingFiltersDtoProps
  }) => {
    if (!data.id) return
    const facturaById = await getFacturasByIdSimple(data.id)
    setModalDocumentoDetail({
      show: true,
      document: facturaById,
    })
  }

  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
  }

  const toggleDeleteModal = (id?: string) => {
    setShowDeleteModal((prevState) => ({
      id: id ?? "",
      show: !prevState.show,
    }))
  }

  const deleteFactura = async () => {
    if (!showDeleteModal?.id) return
    try {
      setDeleting(true)
      const { status }: { status: number } = await deleteFacturaById(
        showDeleteModal.id
      )
      if (status === STATUSCORRECT) {
        toggleDeleteModal()
        notification.success({ body: `${t("crear.factura.delete")}` })
        fetchTableData()
      }
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <div className='tw-flex tw-flex-col tw-w-full tw-shadow-3x2 tw-bg-simple-10 tw-py-3.5 tw-px-8 tw-m-px sm:tw-flex-row sm:tw-justify-between sm:tw-flex-wrap tw-gap-2'>
        <div className='tw-text-xl tw-font-normal tw-flex tw-items-center tw-text-nowrap'>
          {t("crear.factura.buyLabel")} (
          {isLoading ? (
            <Skeleton className='!tw-h-5 !tw-w-8 !tw-mx-1' />
          ) : (
            <span>{totalCount}</span>
          )}
          )
        </div>

        <div className='tw-flex tw-flex-col sm:tw-flex-row sm:tw-justify-start sm:tw-flex-wrap tw-gap-1 lg:tw-gap-3'>
          <FilterMenu
            serviceQuery={setServiceQuery}
            sortingCriteria={sortingCriteria}
            maxResultCount={pagination.itemsPerPage}
            skipCount={pagination.skipCount}
            resetPagination={handleResetPagination}
          />
          <OptionsMenuStorybook
            id='facturas-recibidas'
            items={[
              {
                title: `${t("crear.factura.importLabel")}`,
                hasRedirect: false,
                subMenu: [
                  {
                    title: `${t("crear.factura.importMasive")}`,
                    action: () => history.push(ROUTES.IMPORTAR_FACTURAS),
                    icon: faFileExcel,
                  },
                  {
                    title: `${t("crear.factura.importElectronica")}`,
                    action: () => history.push(ROUTES.IMPORTAR_FACTURAE),
                    icon: faFileXml,
                  },
                  {
                    title: `${t("crear.factura.importEscaner")}`,
                    action: () => history.push(ROUTES.IMPORTAR_FACTURAS_OCR),
                    icon: faFilePdf,
                  },
                ],
              },
              {
                title: `${t("crear.factura.exportLabel")}`,
                hasRedirect: true,
                action: () =>
                  handlerExportFacturasToExcel(
                    TIPO_DOCUMENTO_COMERCIAL.COMPRA,
                    setIsLoadingExport,
                    t
                  ),
                isLoading: isLoadingExport,
                disabled: isLoadingExport,
                icon: faFileExcel,
              },
            ]}
            position='right'
          />
        </div>
      </div>
      <div className='tw-flex tw-flex-col tw-h-[calc(100%-152px)] tw-relative '>
        <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
          <TableERP
            refTable={dtRef}
            columns={COLUMNAS_FACTURAS_RECIBIDAS_ERP({
              t,
              setShowModalDownload,
              setShowModalPagoCobroManual,
              toggleDeleteModal,
            })}
            className='tw-h-full'
            columnResizeMode='expand'
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort
            value={items}
            showGridlines
            dataKey='id'
            lazy
            scrollable
            size='small'
            onRowClick={onRowClick}
            loading={isLoading}
            emptyMessage={
              <NoDataTemplate
                icon={`${STATIC_ASSETS_URL}/Imagenes/Ilustraciones/tablas/facturas-de-compra.svg`}
                title='crear.factura.buyLabel'
                description='shared.noDataDescription'
                actionLabel='crear.factura.addOrder'
                showActions={false}
              />
            }
          />
        </div>
        <PaginationERP
          className={PAGINATION_STYLES}
          totalRecords={totalCount}
          first={pagination.skipCount}
          rows={pagination.itemsPerPage}
          resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
          setPagination={setPagination}
          optionDropdown={[10, 20, 50, 100]}
          texts={{
            textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
            textCurrentPageReport: `${t("pagination.from")}`,
          }}
        />
      </div>
      <ModalTypeDownloadDocument
        modalState={showModalDownload}
        onHide={() =>
          setShowModalDownload({
            show: false,
            id: null,
            serie: null,
          })
        }
        showElements={{
          downloadPdf: true,
          downloadXml: true,
        }}
        typeDocument={TIPO_DOCUMENTO_ENUM.FACTURA}
      />
      <DeleteModal
        visible={showDeleteModal.show}
        onHide={toggleDeleteModal}
        size='small'
        iconType='remesas'
        buttonLabel={t("crear.factura.confirm")}
        title={t("crear.factura.titleDelete")}
        message={t("crear.factura.msgDelete")}
        onConfirm={deleteFactura}
        isLoading={deleting}
      />
      <ModalVerDocumento
        showModal={modalDocumentoDetail.show}
        onHide={() => {
          setModalDocumentoDetail({
            show: false,
            document: null,
          })
        }}
        refreshData={fetchTableData}
        docType={TIPO_DOCUMENTO_ENUM.FACTURA}
        documentoID={modalDocumentoDetail.document?.id ?? null}
        typeDocumentTable='facturas'
        isVenta={false}
      />
      <PagoCobroManualForm
        show={showModalPagoCobroManual.show}
        isVenta={false}
        onHide={() => {
          setShowModalPagoCobroManual({
            show: false,
            id: undefined,
            serie: undefined,
          })
        }}
        document={{
          serie: showModalPagoCobroManual.serie,
          id: showModalPagoCobroManual.id,
        }}
        t={t}
        updateData={fetchTableData}
      />
    </>
  )
}

export default FacturasRecibidas
